import React, { useContext, useState, useCallback, useEffect } from 'react';
import { Search, Plus, UserCog, Download } from 'lucide-react';
import { VIPProfileCard, VIPProfile as CardVIPProfile } from '../components/vip/VIPProfileCard';
import { TablePagination } from '../components/ui/TablePagination';
import { useTeams } from '../contexts/TeamsContext';
import { AuthContext } from '../contexts/AuthContext';
import { getApiConfiguration } from '../api/utils';
import { VipApi, VIPProfile } from 'api-client';
import { RisksStatistics200Response, RiskSeverityEnum, RiskStatusEnum } from 'api-client/models';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import RiskStatisticsPanel from '../components/vip/RiskStatisticsPanel';

const VIPProfiles = () => {
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [profiles, setProfiles] = useState<VIPProfile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState<{
    count: number;
    next: string | null;
    previous: string | null;
  }>({ count: 0, next: null, previous: null });
  const [statistics, setStatistics] = useState<RisksStatistics200Response>({});
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const ITEMS_PER_PAGE = 10; // Standard pagination size

  const fetchProfiles = useCallback(async () => {
    if (!currentTeam?.slug || !token) return;

    setIsLoading(true);
    setError(null);

    try {
      const api = new VipApi(getApiConfiguration(token));
      const response = await api.vipProfilesList({
        teamSlug: currentTeam.slug,
        page: currentPage,
      });
      setProfiles(response.results);
      setPaginationInfo({
        count: response.count,
        next: response.next || null,
        previous: response.previous || null,
      });
    } catch (err) {
      console.error('Error fetching VIP profiles:', err);
      setError('Failed to fetch VIP profiles');
    } finally {
      setIsLoading(false);
    }
  }, [currentTeam?.slug, token, currentPage, searchQuery]);

  // Fetch risk statistics
  const fetchStatistics = useCallback(async () => {
    if (!currentTeam?.slug || !token) return;

    setIsLoadingStats(true);
    try {
      const api = new VipApi(getApiConfiguration(token));
      const response = await api.risksStatistics({
        teamSlug: currentTeam.slug,
      });
      setStatistics(response);
    } catch (err) {
      console.error('Error fetching risk statistics:', err);
    } finally {
      setIsLoadingStats(false);
    }
  }, [currentTeam?.slug, token]);

  useEffect(() => {
    fetchProfiles();
    fetchStatistics();
  }, [fetchProfiles, fetchStatistics]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const transformProfileForCard = (profile: VIPProfile): CardVIPProfile => ({
    name: profile.name,
    profilePicture: profile.profilePicture,
    title: profile.title,
    email: profile.email,
    phoneNumber: profile.phoneNumber,
    activeRisks: typeof profile.activeRisks === 'string' ? parseInt(profile.activeRisks, 10) : profile.activeRisks || 0,
    riskCount: typeof profile.riskCount === 'string' ? parseInt(profile.riskCount, 10) : profile.riskCount || 0,
  });

  // Helper function to get total count from a statistics object
  const getTotalCount = (countObject?: { [key: string]: number }) => {
    if (!countObject) return 0;
    return Object.values(countObject).reduce((sum, count) => sum + count, 0);
  };

  // Helper function to get count for a specific key
  const getCountForKey = (countObject?: { [key: string]: number }, key?: string) => {
    if (!countObject || !key) return 0;
    return countObject[key] || 0;
  };

  // Helper function to get color for severity
  const getSeverityColor = (severity: RiskSeverityEnum) => {
    switch (severity) {
      case RiskSeverityEnum.Critical: return 'text-red-500';
      case RiskSeverityEnum.High: return 'text-orange-500';
      case RiskSeverityEnum.Medium: return 'text-yellow-500';
      case RiskSeverityEnum.Low: return 'text-blue-500';
      default: return 'text-gray-500';
    }
  };

  // Helper function to get color for status
  const getStatusColor = (status: RiskStatusEnum) => {
    switch (status) {
      case RiskStatusEnum.Active: return 'text-blue-500';
      case RiskStatusEnum.Mitigated: return 'text-yellow-500';
      case RiskStatusEnum.Resolved: return 'text-green-500';
      default: return 'text-gray-500';
    }
  };

  // Helper function to get color for source
  const getSourceColor = (source: string) => {
    // Assign different colors to different sources
    const sourceColors: Record<string, string> = {
      'osint': 'text-blue-500',
      'dark_web': 'text-purple-500',
      'social_media': 'text-pink-500',
      'data_breach': 'text-red-500',
      'internal': 'text-green-500',
    };
    
    return sourceColors[source.toLowerCase()] || 'text-gray-400';
  };

  // Helper function to get color for type
  const getTypeColor = (type: string) => {
    // Assign different colors to different types
    const typeColors: Record<string, string> = {
      'personal_info': 'text-blue-500',
      'credentials': 'text-red-500',
      'financial': 'text-green-500',
      'communication': 'text-yellow-500',
      'location': 'text-purple-500',
    };
    
    return typeColors[type.toLowerCase()] || 'text-indigo-500';
  };

  // Helper function to format label
  const formatLabel = (label: string) => {
    return label.replace(/_/g, ' ').split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <UserCog className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">VIP Profiles</h1>
            <p className="text-sm text-gray-400">Manage and monitor your VIP profiles</p>
          </div>
        </div>
        {/* <div className="flex items-center space-x-3">
          <button
            onClick={() => console.log('Export')}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed"
            disabled
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </button>
          <button className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors disabled:bg-gray-700 disabled:text-gray-400">
            <Plus className="h-4 w-4" />
            <span>Add Profile</span>
          </button>
        </div> */}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">
          {error}
        </div>
      )}

            {/* Statistics Dashboard */}
      <RiskStatisticsPanel 
        statistics={statistics} 
        isLoading={isLoadingStats} 
        onRefresh={fetchStatistics} 
        title="Risk Overview"
      />


      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search VIP profiles..."
          value={searchQuery}
          onChange={handleSearch}
          className="w-full pl-10 pr-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* VIP Profiles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          <div>Loading...</div>
        ) : (
          profiles.map((profile) => (
            <Link
              key={profile.id}
              to={`/vip-profiles/${profile.id}`}
              className="block transition-transform hover:scale-[1.02]"
            >
              <VIPProfileCard
                profile={transformProfileForCard(profile)}
              />
            </Link>
          ))
        )}
      </div>

      {/* Pagination */}
      <TablePagination
        currentPage={currentPage}
        totalItems={paginationInfo.count}
        onPageChange={handlePageChange}
        itemsPerPage={ITEMS_PER_PAGE}
        paginationInfo={paginationInfo}
        itemName="VIP profiles"
      />
    </div>
  );
};

export default VIPProfiles;
